import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "react-toastify";
import { SCAN_PATH, parseScanParams } from "./stockQrUtils";
import "./StockQR.scss";

const READER_ID = "stock-qr-reader";

/**
 * In-app camera scanner. Needs HTTPS (or localhost) for camera access.
 * onScan(params) receives the parsed QR content (same shape as parseScanParams()).
 */
const StockQRScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let scanner = null;
    let startPromise = null;

    // The browser reports "play() request was interrupted" when the <video> is removed
    // while the camera is still starting (React dev mode mounts, unmounts, remounts).
    // It is harmless, so keep it out of the error overlay.
    const swallowPlayAbort = (event) => {
      const reason = event.reason;
      if (
        reason?.name === "AbortError" ||
        /play\(\) request was interrupted/i.test(reason?.message || "")
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("unhandledrejection", swallowPlayAbort, true);

    const handleDecoded = (text) => {
      if (handledRef.current) return;
      let url;
      try {
        url = new URL(text);
      } catch {
        return; // not a URL, keep scanning
      }
      if (url.pathname !== SCAN_PATH) {
        toast.info("That QR code is not a stock QR.", {
          toastId: "not-stock-qr",
        });
        return;
      }
      const params = parseScanParams(url.search);
      if (!params.combinationIds.length) return;
      handledRef.current = true;
      onScan(params);
    };

    // Start slightly later: a dev-mode remount cancels this timer, so only one camera start happens
    const timer = setTimeout(() => {
      scanner = new Html5Qrcode(READER_ID);
      scannerRef.current = scanner;
      startPromise = scanner
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          handleDecoded,
          () => {}, // per-frame "no QR found" noise
        )
        .catch((e) => {
          if (cancelled || e?.name === "AbortError") return;
          console.error("Camera start failed", e);
          setError(
            "Could not open the camera. Allow camera access and make sure the page is opened over HTTPS.",
          );
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (!scanner) {
        window.removeEventListener(
          "unhandledrejection",
          swallowPlayAbort,
          true,
        );
        return;
      }
      // Wait for the start to finish before stopping, so the video is never removed mid-play
      Promise.resolve(startPromise)
        .then(() => (scanner.isScanning ? scanner.stop() : null))
        .catch(() => {})
        .finally(() => {
          try {
            scanner.clear();
          } catch {
            /* already cleared */
          }
          setTimeout(
            () =>
              window.removeEventListener(
                "unhandledrejection",
                swallowPlayAbort,
                true,
              ),
            500,
          );
        });
    };
  }, [onScan]);

  return (
    <div
      className="scan-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="scan-popup" onClick={(e) => e.stopPropagation()}>
        <header className="scan-header">
          <div>
            <span className="scan-kind">Scanner</span>
            <h3>Scan a stock QR code</h3>
          </div>
          <button
            type="button"
            className="scan-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="scan-body">
          <div id={READER_ID} className="qr-reader" />
          {error ? (
            <p className="scan-error">{error}</p>
          ) : (
            <p className="scan-hint-center">
              Point the camera at a product or variant QR code.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockQRScanner;
